import {
  Avatar,
  Button,
  Empty,
  Input,
  Modal,
  Spin,
  Tooltip,
  message,
} from 'antd'
import {
  FileExcelOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { apiUrl } from '../../../config'
import AppPagination from '../../../components/Common/AppPagination'
import { AppPaginationWrapper } from '../../../components/Common/AppPaginationStyled'
import authenticationStore from '../../../stores/authenticationStore'
import contactStore from '../contactStore'
import {
  ContactLayout,
  ContactPanel,
  ContactDetailModalStyle,
  DepartmentButton,
  DepartmentList,
  DepartmentNav,
  EmployeeCard,
  EmployeeContactRow,
  EmployeeContactText,
  EmployeeGrid,
  EmployeeMeta,
  EmployeeName,
  EmployeeRole,
  ListDepartmentContent,
  PanelHeader,
  PanelTitle,
  SearchActions,
} from './ListDepartmentStyled'
import UserDetail from './UserDetail'
import {
  CONTACT_ALL_DEPARTMENT_KEY,
  CONTACT_ALL_DEPARTMENT_LABEL,
  CONTACT_EMPLOYEE_EXPORT_CONFIG,
  getContactInitialName,
} from '../contactConstants'
import { exportContactsToExcel } from '../contactExportExcelHelper'

const ListDepartmentPage = listDepartmentPageProps => {
  const {
    departmentStore,
    userStore,
    loadingAnimationStore,
  } = listDepartmentPageProps
  const { departmentList } = departmentStore
  const { companyCode } = contactStore

  const {
    userOfDepartmentListTotalCount,
    userOfDepartmentListPageIndex,
    userOfDepartmentListPageSize,
  } = userStore

  const [activeDepartmentCode, setActiveDepartmentCode] =
    useState(CONTACT_ALL_DEPARTMENT_KEY)
  const [keyword, setKeyword] = useState('')
  const [userOfDepartmentData, setUserOfDepartmentData] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [isDetailVisible, setIsDetailVisible] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const departmentSignature = departmentList
    .map(item => `${item.code}:${item.name}`)
    .join('|')

  const departments = useMemo(
    () => [
      {
        name: CONTACT_ALL_DEPARTMENT_LABEL,
        code: CONTACT_ALL_DEPARTMENT_KEY,
      },
      ...departmentList,
    ],
    [departmentList, departmentSignature]
  )

  const activeDepartmentName = useMemo(
    () =>
      activeDepartmentCode === CONTACT_ALL_DEPARTMENT_KEY
        ? CONTACT_ALL_DEPARTMENT_LABEL
        : departments.find(item => item.code === activeDepartmentCode)?.name ||
          CONTACT_ALL_DEPARTMENT_LABEL,
    [activeDepartmentCode, departments]
  )

  const setDepartmentFilter = useCallback(
    departmentCode => {
      userStore.setFilter(
        'userOfDepartmentListByDepartmentCode',
        departmentCode === CONTACT_ALL_DEPARTMENT_KEY ? null : departmentCode
      )
    },
    [userStore]
  )

  const fetchUsersByDepartment = useCallback(
    () =>
      userStore.getUserListByDepartment().then(response => {
        setUserOfDepartmentData(response.data)
        return response
      }),
    [userStore]
  )

  const onExportToExcel = useCallback(() => {
    if (isExporting || loadingAnimationStore.tableLoading) return
    setIsExporting(true)
    loadingAnimationStore.setTableLoading(true)
    userStore
      .getAllUserListByDepartment()
      .then(response =>
        exportContactsToExcel({
          tableData: response.data,
          ...CONTACT_EMPLOYEE_EXPORT_CONFIG,
        })
      )
      .catch(() => {
        message.warning('Lỗi lấy thông tin người dùng trong phòng ban!')
      })
      .finally(() => {
        setIsExporting(false)
        loadingAnimationStore.setTableLoading(false)
      })
  }, [isExporting, loadingAnimationStore, userStore])

  const onChangeDepartment = useCallback(
    departmentCode => {
      setActiveDepartmentCode(departmentCode)
      setKeyword('')
      setSelectedUser(null)
      setIsDetailVisible(false)
      setDepartmentFilter(departmentCode)
      userStore.setFilter('userOfDepartmentListPageIndex', 0)
      userStore.setFilter('userListPageIndex', 0)
      userStore.setFilter('userOfDepartmentListHasAdmin', true)
      userStore.clearUserListKeyword()
      loadingAnimationStore.setTableLoading(true)
      fetchUsersByDepartment()
        .catch(() => {
          message.warning('Lỗi lấy thông tin người dùng trong phòng ban!')
        })
        .finally(() => {
          loadingAnimationStore.setTableLoading(false)
        })
    },
    [fetchUsersByDepartment, loadingAnimationStore, setDepartmentFilter, userStore]
  )

  const onChangePagination = useCallback(
    (pageIndex, pageSize) => {
      setSelectedUser(null)
      setIsDetailVisible(false)
      userStore.setFilter('userOfDepartmentListPageIndex', pageIndex - 1)
      userStore.setFilter('userOfDepartmentListPageSize', pageSize)
      userStore.setFilter('userOfDepartmentListHasAdmin', true)
      loadingAnimationStore.setTableLoading(true)
      fetchUsersByDepartment()
        .catch(() => {
          message.warning('Lỗi lấy thông tin người dùng trong phòng ban!')
        })
        .finally(() => {
          loadingAnimationStore.setTableLoading(false)
        })
    },
    [fetchUsersByDepartment, loadingAnimationStore, userStore]
  )

  const selectUser = useCallback(
    record => {
      setSelectedUser(record)
      setIsDetailVisible(true)
      userStore.clearAvatarByUserCode()
      if (record?.code) {
        userStore.getAvatarByUserCode(record.code).catch(() => undefined)
      }
    },
    [userStore]
  )

  const onSearch = useCallback(
    value => {
      const nextKeyword = value?.trim() || ''
      setKeyword(nextKeyword)
      userStore.setUserListKeyword(nextKeyword)
      userStore.setFilter('userOfDepartmentListPageIndex', 0)
      userStore.setFilter('userListKeyword', nextKeyword)
      userStore.setFilter('userOfDepartmentListHasAdmin', true)
      loadingAnimationStore.setTableLoading(true)
      fetchUsersByDepartment()
        .catch(() => {
          message.error('Đã xảy ra lỗi!')
        })
        .finally(() => {
          loadingAnimationStore.setTableLoading(false)
        })
    },
    [fetchUsersByDepartment, loadingAnimationStore, userStore]
  )

  const onSearchIconClick = useCallback(() => {
    onSearch(keyword)
  }, [keyword, onSearch])

  const onKeywordChange = useCallback(event => {
    setKeyword(event.target.value)
  }, [])

  const onCloseDetail = useCallback(() => {
    setIsDetailVisible(false)
  }, [])

  useEffect(() => {
    if (!authenticationStore.currentUser || !companyCode) {
      setUserOfDepartmentData([])
      setSelectedUser(null)
      setIsDetailVisible(false)
      departmentStore.clearDepartmentList()
      return undefined
    }

    loadingAnimationStore.showSpinner(true)
    setActiveDepartmentCode(CONTACT_ALL_DEPARTMENT_KEY)
    setKeyword('')
    setSelectedUser(null)
    setIsDetailVisible(false)
    userStore.clearUserListKeyword()
    departmentStore
      .getAllDepartmentList(companyCode)
      .then(() => {
        setDepartmentFilter(CONTACT_ALL_DEPARTMENT_KEY)
        userStore.setFilter('userOfDepartmentListPageIndex', 0)
        userStore.setFilter('userListPageIndex', 0)
        userStore.setFilter(
          'userOfDepartmentListSort',
          'department-asc, position-asc'
        )
        userStore.setFilter('userOfDepartmentListHasAdmin', true)
        return fetchUsersByDepartment()
      })
      .catch(() => {
        message.warning('Lỗi không tìm thấy thông tin!')
      })
      .finally(() => {
        loadingAnimationStore.showSpinner(false)
      })

    return () => {
      departmentStore.clearDepartmentList()
    }
  }, [
    authenticationStore.currentUser,
    companyCode,
    departmentStore,
    fetchUsersByDepartment,
    loadingAnimationStore,
    setDepartmentFilter,
    userStore,
  ])

  if (!companyCode) {
    return <Empty description="Vui lòng chọn công ty để xem danh bạ" />
  }

  return (
    <ListDepartmentContent>
      <ContactDetailModalStyle />
      <ContactLayout>
        <DepartmentNav>
          <DepartmentList>
            {departments.map(department => (
              <Tooltip title={department.name} key={department.code}>
                <DepartmentButton
                  htmlType="button"
                  className={
                    activeDepartmentCode === department.code ? 'active' : ''
                  }
                  onClick={() => onChangeDepartment(department.code)}>
                  <span>{department.name}</span>
                </DepartmentButton>
              </Tooltip>
            ))}
          </DepartmentList>
        </DepartmentNav>

        <ContactPanel>
          <PanelHeader>
            <PanelTitle>
              <span className="title-text">{activeDepartmentName}</span>
              {userOfDepartmentListTotalCount !== undefined &&
                userOfDepartmentListTotalCount !== null && (
                  <span className="count-badge">
                    {userOfDepartmentListTotalCount} nhân sự
                  </span>
                )}
            </PanelTitle>
            <SearchActions>
              <Input
                allowClear
                placeholder="Tìm kiếm theo họ tên..."
                suffix={
                  <SearchOutlined
                    className="contact-search-icon"
                    onClick={onSearchIconClick}
                  />
                }
                value={keyword}
                onChange={onKeywordChange}
                onPressEnter={onSearchIconClick}
              />
              <Button
                icon={<FileExcelOutlined className="excel-icon" />}
                disabled={isExporting || loadingAnimationStore.tableLoading}
                onClick={onExportToExcel}>
                In file Excel
              </Button>
            </SearchActions>
          </PanelHeader>

          <Spin spinning={loadingAnimationStore.tableLoading}>
            <EmployeeGrid>
              {userOfDepartmentData.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có người dùng"
                />
              ) : (
                userOfDepartmentData.map(user => (
                  <EmployeeCard
                    key={user.code || user.username}
                    onClick={() => selectUser(user)}>
                    <Avatar
                      className="card-avatar"
                      size={76}
                      src={
                        user.imageId &&
                        `${apiUrl}/api/v1/images/${user.imageId}`
                      }>
                      {getContactInitialName(user)}
                    </Avatar>
                    <Tooltip title={user.name_lowercase}>
                      <EmployeeName className="employee-name">
                        {user.name_lowercase}
                      </EmployeeName>
                    </Tooltip>
                    <Tooltip title={user?.position?.name}>
                      <EmployeeRole>
                        {user?.position?.name || 'Chưa cập nhật'}
                      </EmployeeRole>
                    </Tooltip>
                    <EmployeeMeta>
                      <EmployeeContactRow>
                        <MailOutlined />
                        <Tooltip title={user.email}>
                          <EmployeeContactText>
                            {user.email || 'Chưa cập nhật'}
                          </EmployeeContactText>
                        </Tooltip>
                      </EmployeeContactRow>
                      <EmployeeContactRow>
                        <PhoneOutlined />
                        <Tooltip title={user.phone}>
                          <EmployeeContactText>
                            {user.phone || 'Chưa cập nhật'}
                          </EmployeeContactText>
                        </Tooltip>
                      </EmployeeContactRow>
                    </EmployeeMeta>
                  </EmployeeCard>
                ))
              )}
            </EmployeeGrid>
          </Spin>

          <AppPaginationWrapper>
            <AppPagination
              onChange={onChangePagination}
              total={userOfDepartmentListTotalCount}
              pageSize={userOfDepartmentListPageSize}
              current={userOfDepartmentListPageIndex + 1}
            />
          </AppPaginationWrapper>
        </ContactPanel>
      </ContactLayout>

      <Modal
        title="Thông tin nhân viên"
        visible={isDetailVisible}
        footer={null}
        width={650}
        destroyOnClose
        wrapClassName="contact-detail-modal"
        onCancel={onCloseDetail}>
        <UserDetail selectedUser={selectedUser} />
      </Modal>
    </ListDepartmentContent>
  )
}

ListDepartmentPage.propTypes = {}

export default memo(
  inject(
    'departmentStore',
    'userStore',
    'loadingAnimationStore'
  )(observer(ListDepartmentPage))
)
